import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Save, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { imgSrc } from "../../lib/siteContent";
import ImageUploadField from "./ImageUploadField";

function PortfolioForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({ title: "", category: "", image: "", ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title, category: form.category, image: form.image };
      if (initial?.id) await api.put(`/admin/portfolio/${initial.id}`, payload);
      else await api.post("/admin/portfolio", payload);
      toast.success("Work item saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-6 border border-[#00D4FF]/30" data-testid="portfolio-form">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit work item" : "New work item"}</h3>
        <button onClick={onCancel} className="text-[#5A5A72] hover:text-[#0E0E1A]"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Title</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="portfolio-title-input" className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Category / caption</label>
          <input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="portfolio-category-input" className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm" />
        </div>
        <div className="md:col-span-2">
          <ImageUploadField label="Work image" value={form.image} onChange={(v) => set("image", v)} testId="portfolio-image" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} className="btn-ghost px-4 py-2 rounded-lg text-sm">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="portfolio-save-btn" className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

export default function AdminPortfolioManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete this work item?")) return;
    try {
      await api.delete(`/admin/portfolio/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (editing !== null) {
    return <PortfolioForm initial={editing} onCancel={() => setEditing(null)} onSaved={onSaved} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5A5A72]">{items.length} work items shown on the site</p>
        <button onClick={() => setEditing({})} data-testid="add-portfolio-btn" className="btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add work
        </button>
      </div>
      {loading ? (
        <div className="glass-card rounded-2xl p-10 text-center text-[#5A5A72]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center" data-testid="portfolio-manager-empty">
          <ImageIcon className="w-10 h-10 mx-auto text-[#9191A8] mb-3" />
          <p className="font-display text-lg">No work items yet.</p>
          <p className="text-[#6B7280] text-sm mt-1">Add your real projects — they'll appear on the homepage and Work page.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`portfolio-manage-${p.id}`} className="glass-card rounded-2xl overflow-hidden">
              <div className="relative h-40 bg-white">
                {p.image && <img src={imgSrc(p.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="p-4">
                <p className="font-display font-bold">{p.title}</p>
                <p className="text-xs font-mono text-[#00D4FF]">{p.category}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(p)} data-testid={`portfolio-edit-${p.id}`} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => remove(p.id)} data-testid={`portfolio-delete-${p.id}`} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
